import { useMsal } from '@azure/msal-react';
import { IBreadCrumbs } from '@components/breadcumbs/BreadCumbs';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError, pushSuccess } from '@components/toast';
import {
    cameraController,
    cameraViewController,
    geoZoneController,
    geoZoneOperationConfigController,
    locationController,
    sp111ConfigureRequestHttpController,
} from '@controllers/index';
import { Paging } from '@Core';
import { IGetGeoZoneProps } from '@EcamModel/controllers/IGeoZoneHttpController';
import { ISendActionGet } from '@EcamModel/controllers/ISP111ConfigureHttpController';
import {
    CameraWithInfo,
    ContraventionReasonTranslations,
    GeoZoneCoordinates,
    GeoZoneOperationConfigWithGracePeriod,
    GeoZones,
    GeoZoneType,
    Status,
    Location,
} from '@EcamModel/model';
import { CameraView } from '@EcamModel/model/CameraView';
import { Zones } from '@EcamModel/model/Zones';
import usePopUp from '@hooks/usePopUp';
import useScrollToTop from '@hooks/useScrollToTop';
import { Mode } from '@pages/cameras/details';
import { detectModes, FormValues } from '@pages/cameras/details/components/EditGeoZones';
import { initPaging } from '@pages/cameras/list';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import _, { groupBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cameraViewBlob } from '../..';
import { base64ToHTMLImageElement } from '../../components/PopupConfigViewCameraPTZ';
import { UpsertGeoZoneProps } from './useAddGeoZones';

export default function useEditGeoZones({ isCheckGeoZoneDetail = false }: UpsertGeoZoneProps) {
    const navigate = useNavigate();
    useScrollToTop();
    const queryClient = useQueryClient();

    const popUpEditablePeriods = usePopUp();
    const popUpDeletePeriods = usePopUp();
    const params = useParams<{ id: string; geoZoneId: string }>();
    const geoZoneId = parseInt(params.geoZoneId!);

    const location = useLocation();
    const state = location?.state;
    const geoZonesItem = state?.geoZonesItem as GeoZones;

    const [selectedGeoZone, setSelectedGeoZone] = useState<GeoZones>({} as GeoZones);

    const frameIndexItem = state?.frameIndex;

    const [cameraView, setCameraView] = useState<CameraView>(state?.cameraView);

    const [camera, setCamera] = useState<CameraWithInfo>({} as CameraWithInfo);
    const [filteredListGeoZones, setFilteredListGeoZones] = useState<IGetGeoZoneProps>({} as IGetGeoZoneProps);
    const [loadingListGzByView, setLoadingListGzByView] = useState(true);
    const [listGeoZonesByView, setListGeoZonesByView] = useState<Paging<GeoZones>>({ ...initPaging });
    const [loading, setLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [contraventionReasons, setContraventionReasons] = useState<ContraventionReasonTranslations[]>([]);
    const [mode, setMode] = useState<Mode>();
    const [selectedTab, setSelectedTab] = useState(1);
    const [zones, setZones] = useState<Zones[]>([]);

    const [selectedScheduleOperationHours, setSelectedScheduleOperationHours] = useState<number | undefined>(undefined);

    const [scheduleOperationConfigs, setScheduleOperationConfigs] = useState<GeoZoneOperationConfigWithGracePeriod[]>(
        []
    );

    const pathNameBreadCrumbs = `/cameras`;

    const breadcrumbs: IBreadCrumbs[] = [
        { id: 1, title: 'Camera list', href: `${pathNameBreadCrumbs}` },
        { id: 2, title: `${camera.Name}`, href: `${pathNameBreadCrumbs}/${params.id}` },
        {
            id: 3,
            title: `Geo-zones overview ${frameIndexItem + 1}`,
            onBack: true,
        },
        { id: 4, title: `Edit geo-zones`, isColorBlackText: true },
    ];

    const isHasFrameIndexItem = frameIndexItem !== undefined;
    const conditionBreadcrumbs = isHasFrameIndexItem ? breadcrumbs : breadcrumbs.filter((item) => item.id !== 3);

    const currentGeoZoneConfigs = selectedGeoZone.GZOConfigs?.[0];

    const currentOperationHours = currentGeoZoneConfigs?.GZOConfigGracePeriods.flatMap((item) =>
        item.GZOConfigDays.map((item) => item)
    ).filter((item) => !!item.Status);
    const _currentOperationHours = groupBy(currentOperationHours, 'Weekday');

    const currentSelectedPeriodConfigs = selectedGeoZone.GZOConfigs?.[0]?.GZOConfigGracePeriods;

    const nextGeoZoneConfigs = scheduleOperationConfigs.find((item) => item.Id === selectedScheduleOperationHours!);

    const nextOperationHours = nextGeoZoneConfigs?.GZOConfigGracePeriods.flatMap((item) =>
        item.GZOConfigDays.map((item) => item)
    ).filter((item) => !!item.Status);

    const nextPeriodConfigs = nextGeoZoneConfigs?.GZOConfigGracePeriods;

    const _nextOperationHours = groupBy(nextOperationHours, 'Weekday');

    const isEdit = moment(nextGeoZoneConfigs?.EffectiveFrom).startOf('date').isAfter(moment().startOf('date'));

    const methods = useForm<FormValues>({
        // resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            contraventionReasonTranslations: undefined,
            detectMode: undefined,
            isActive: true,
            geoZonePoints: [],
            isExemptVRNs: false,
            geoZoneName: '',
            totalBay: 0,
            location: undefined,
            zone: undefined,
        },
    });

    const {
        watch,
        reset,
        control,
        setValue: _setValue,
        handleSubmit,
        formState: { isDirty, isValid, errors, ...formState },
    } = methods;

    const setValue = _setValue as (name: string, value: any, options?: Record<string, boolean>) => void;

    const prevGeoZonePoints: (GeoZoneCoordinates & { GeoZone: GeoZones })[][] = listGeoZonesByView.rows?.map((gz) => {
        return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index)
            .concat(gz.GeoZoneCoordinates[0])
            .map((coord) => ({ ...coord, GeoZone: gz })) as (GeoZoneCoordinates & { GeoZone: GeoZones })[];
    }) as (GeoZoneCoordinates & { GeoZone: GeoZones })[][];

    const filterPreGeoZones = prevGeoZonePoints?.filter((item) => {
        return item[0]?.GeoZone?.Id !== (geoZonesItem?.Id ?? geoZoneId);
    });
    const prevGeoZonePointsCameraSP111: GeoZoneCoordinates[][] =
        (camera.GeoZones?.map((gz) => {
            return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
        }) as GeoZoneCoordinates[][]) ?? [];

    const filterPreGeoZonesCameraSP111 = prevGeoZonePointsCameraSP111?.map((item) =>
        JSON.stringify(item) !==
        JSON.stringify(
            selectedGeoZone.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index)?.concat(
                selectedGeoZone.GeoZoneCoordinates[0]
            )
        )
            ? item
            : [
                  { Index: 1, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
                  { Index: 2, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
                  { Index: 3, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
                  { Index: 4, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
              ]
    );

    const contraventionAutocomplete = useStyledAutocomplete({
        list: {
            options: contraventionReasons,
            isFiltered(option, searchText) {
                return option.Summary.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [contraventionReasons],
    });
    const detectModeAutocomplete = useStyledAutocomplete({
        list: {
            options: detectModes,
            isFiltered(option, searchText) {
                return option.Label.toLowerCase().includes(searchText.toLowerCase());
            },
        },
    });

    const availableLocations = useMemo(() => {
        const locationMap = new Map<number, Location>();

        if (camera.Zone?.Location) {
            const primaryLoc = camera.Zone.Location;
            locationMap.set(primaryLoc.Id!, {
                ...primaryLoc,
                Zones: [camera.Zone],
            });
        }

        if (camera.ExtendedCoverage && Array.isArray(camera.ExtendedCoverage)) {
            (camera.ExtendedCoverage as Location[]).forEach((extLoc) => {
                const existingLoc = locationMap.get(extLoc.Id!);

                if (existingLoc) {
                    const mergedZones = [...(existingLoc.Zones || [])];
                    (extLoc.Zones || []).forEach((zone) => {
                        if (!mergedZones.some((z) => z.Id === zone.Id)) {
                            mergedZones.push(zone);
                        }
                    });
                    locationMap.set(extLoc.Id!, {
                        ...extLoc,
                        Zones: mergedZones,
                    });
                } else {
                    locationMap.set(extLoc.Id!, extLoc);
                }
            });
        }

        return Array.from(locationMap.values());
    }, [camera.Zone, camera.ExtendedCoverage]);

    const locationAutocomplete = useStyledAutocomplete({
        list: {
            options: availableLocations,
            isFiltered(option: Location, searchText) {
                return option.Name.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [availableLocations],
    });

    const zoneAutocomplete = useStyledAutocomplete({
        list: {
            options: zones,
            isFiltered(option, searchText) {
                return option.PublicName.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [zones],
    });

    const navigateGeoZonesTab = `${pathNameBreadCrumbs}/${params.id}`;

    const handleNavigateButton = () => {
        navigate(navigateGeoZonesTab, {
            state: {
                tabValue: !_.isNil(camera.PTZCameraId) ? 2 : 1,
                frameIndex: frameIndexItem,
                cameraViewId: cameraView?.Id,
            },
        });
    };

    const handleEditGeoZoneDetail = (_geoZones: GeoZones) => {
        setIsLoadingSave(true);
        geoZoneController
            .upsert(_geoZones)
            .then((_) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['patrolTimeForecast']);
                handleNavigateButton();
                pushSuccess('Geo-zone saved successfully');
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoadingSave(false);
            });
    };

    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

    const handleEditGeoZoneOverview = async (_filter: GeoZones) => {
        setIsLoadingSave(true);
        await geoZoneController
            .upsertGeoZoneOverview(_filter)
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                navigate(`${pathNameBreadCrumbs}/${params.id}`, {
                    state: { tabValue: !_.isNil(camera.PTZCameraId) ? 2 : 1, cameraViewId: cameraView?.Id },
                });
                pushSuccess('Geo-zone saved successfully');
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoadingSave(false);
            });
    };

    const { accounts } = useMsal();

    const handleEditGeoZone = handleSubmit((data) => {
        const activeGeoZone = geoZonesItem ?? selectedGeoZone;
        if (activeGeoZone?.GeoZoneType === GeoZoneType.Overview) {
            const _geoZones: GeoZones = {
                Id: geoZoneId,
                CameraId: camera.Id!,
                CameraViewId: cameraView?.Id,
                GeoZoneType: GeoZoneType.Overview,
                UpdatedBy: accounts?.[0].username,
                GeoZoneCoordinates: data.geoZonePoints.slice(0, data.geoZonePoints.length - 1).map((item) => ({
                    GeoZoneId: item.GeoZoneId,
                    Index: item.Index,
                    XPoint: item.XPoint,
                    YPoint: item.YPoint,
                })),
                GeoZoneName: data.geoZoneName.trim() || '',
            };
            handleEditGeoZoneOverview(_geoZones);
        } else if (activeGeoZone?.GeoZoneType === GeoZoneType.Detail || isCheckGeoZoneDetail) {
            const _geoZones: GeoZones = {
                Id: activeGeoZone?.Id || selectedGeoZone.Id,
                CameraId: camera.Id!,
                UpdatedBy: accounts?.[0].username,
                ContraventionReasonId: Number(data.contraventionReasonTranslations.Id!),
                Status: !!data.isActive ? Status.Active : Status.Inactive,
                ValidationType: data.detectMode.Value,
                GeoZoneCoordinates: data.geoZonePoints.slice(0, data.geoZonePoints.length - 1).map((item) => ({
                    GeoZoneId: item.GeoZoneId,
                    Index: item.Index,
                    XPoint: item.XPoint,
                    YPoint: item.YPoint,
                })),
                PermitExemptionsEnabled: data.isExemptVRNs,
                TotalBay: data.totalBay || 0,
                GeoZoneName: data.geoZoneName.trim() || '',
                ZoneId: data.zone?.Id,
            };
            handleEditGeoZoneDetail(_geoZones);
        }
    });

    const handleConfirm = async (data: GeoZoneOperationConfigWithGracePeriod) => {
        setIsLoading(true);
        await geoZoneOperationConfigController
            .upsertGzoConfig(data)
            .then(async (res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['patrolTimeForecast']);
                setScheduleOperationConfigs(res);
                await geoZoneController
                    .get(
                        !isHasFrameIndexItem || isCheckGeoZoneDetail || !geoZonesItem ? params.geoZoneId! : geoZonesItem?.Id?.toString()!
                    )
                    .then((res) => setSelectedGeoZone(res));
                popUpEditablePeriods.onClose();
            })
            .finally(() => {
                pushSuccess('Update configs successfully');
                setIsLoading(false);
            });
    };

    const handleDelete = async (id: number) => {
        setIsLoading(true);
        await geoZoneOperationConfigController
            .deleteGzoConfig(id)
            .then(async (res) => {
                setScheduleOperationConfigs(res);
                setSelectedScheduleOperationHours(res.at(0)?.Id || -1);
                await geoZoneController
                    .get(
                        !isHasFrameIndexItem || isCheckGeoZoneDetail || !geoZonesItem ? params.geoZoneId! : geoZonesItem?.Id?.toString()!
                    )
                    .then((res) => setSelectedGeoZone(res));
                popUpDeletePeriods.onClose();
            })
            .finally(() => {
                setIsLoading(false);
                pushSuccess('Delete configs successfully');
            });
    };
    useEffect(() => {
        geoZoneController.getContraventionReasonTranslations().then((res) => {
            setContraventionReasons(res);
        });

        const _detectMode = detectModes.find((m) => m.Value === selectedGeoZone.ValidationType);
        reset({
            contraventionReasonTranslations: selectedGeoZone.ContraventionReason?.ContraventionReasonTranslations?.[0],
            detectMode: _detectMode,
            isActive: Boolean(selectedGeoZone.Status === Status.Active),
            isExemptVRNs: Boolean(selectedGeoZone.PermitExemptionsEnabled),
            geoZonePoints:
                geoZonesItem?.GeoZoneCoordinates?.concat(geoZonesItem?.GeoZoneCoordinates[0]) ||
                selectedGeoZone?.GeoZoneCoordinates?.concat(selectedGeoZone?.GeoZoneCoordinates),
            geoZoneName: selectedGeoZone?.GeoZoneName || '',
            totalBay: selectedGeoZone?.TotalBay || 0,
        });
    }, [
        reset,
        selectedGeoZone.ContraventionReason?.ContraventionReasonTranslations,
        geoZonesItem?.GeoZoneCoordinates,
        selectedGeoZone.Status,
        selectedGeoZone.GeoZoneCoordinates,
        selectedGeoZone.PermitExemptionsEnabled,
        selectedGeoZone.ValidationType,
        selectedGeoZone?.GeoZoneName,
    ]);

    useEffect(() => {
        const zoneToUse = selectedGeoZone.Zone || camera.Zone;
        
        if (zoneToUse?.Location && availableLocations.length > 0) {
            const mergedLocation = availableLocations.find((loc) => loc.Id === zoneToUse.Location?.Id);
            
            if (mergedLocation) {
                setValue('location', mergedLocation);
                setZones(mergedLocation.Zones || []);
                
                const currentZone = mergedLocation.Zones?.find((z) => z.Id === zoneToUse.Id);
                setValue('zone', currentZone || zoneToUse);
            }
        }
    }, [selectedGeoZone.Zone, camera.Zone, availableLocations, setValue]);

    const { image: imageElement, loading: loadingImage } = useImageDimensions(
        `${cameraView?.LastPhotoBlob}?${JSON.stringify(cameraView?.Updated)}`,
        cameraViewBlob
    );
    const [blobBase64SP111, setBlobBase64SP111] = useState<string>('');
    const [imageElementSP111, setImageElementSP111] = useState<HTMLImageElement | null>(null);

    const getPhotoSnapShotSP111 = async (_filteredCamera: ISendActionGet) => {
        await sp111ConfigureRequestHttpController
            .getPhotoSnapShot(_filteredCamera)
            .then((res) => {
                setBlobBase64SP111(res);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            });
    };

    useEffect(() => {
        setLoading(true);
        const init = async () => {
            await cameraController
                .get(params.id!)
                .then(async (res) => {
                    const shouldFetchGeoZoneDetail = geoZonesItem?.GeoZoneType === GeoZoneType.Detail
                        || isCheckGeoZoneDetail
                        || !geoZonesItem;

                    if (shouldFetchGeoZoneDetail) {
                        const fetchId = !isHasFrameIndexItem || isCheckGeoZoneDetail || !geoZonesItem
                            ? params.geoZoneId!
                            : geoZonesItem?.Id?.toString()!;
                        const fetchIdNum = !isHasFrameIndexItem || isCheckGeoZoneDetail || !geoZonesItem
                            ? geoZoneId
                            : geoZonesItem?.Id!;

                        await geoZoneController
                            .get(fetchId)
                            .then((res) => setSelectedGeoZone(res));
                        await geoZoneOperationConfigController
                            .getPastOrFuturePeriods(fetchIdNum)
                            .then((res) => {
                                setScheduleOperationConfigs(res);
                                setSelectedScheduleOperationHours(res.at(0)?.Id || -1);
                            });
                    }

                    if (res.PTZCameraId) {
                        const targetGeoZoneId = isCheckGeoZoneDetail ? Number(geoZoneId) : (geoZonesItem?.Id ?? geoZoneId);
                        const matchedGeoZone = res.GeoZones?.find((item) => item.Id === targetGeoZoneId);
                        const viewId = cameraView?.Id
                            ?? geoZonesItem?.CameraViewId
                            ?? matchedGeoZone?.CameraViewId;

                        const filteredListGeoZones: IGetGeoZoneProps = {
                            pageSize: 10000,
                            filter: {
                                CameraId: res.Id!,
                                CameraViewId: viewId,
                            },
                        };
                        setSelectedGeoZone(matchedGeoZone || ({} as GeoZones));
                        setFilteredListGeoZones(filteredListGeoZones);
                    }

                    setCamera(res);
                    setLoading(false);

                    if (isCheckGeoZoneDetail) {
                        const filteredCamera: ISendActionGet = {
                            ip: res.IP,
                            authenticate: {
                                password: res.PassWord,
                                username: res.UserName,
                            },
                        };
                        getPhotoSnapShotSP111(filteredCamera);
                    }
                })
                .catch((err) => {
                    navigate(`${pathNameBreadCrumbs}`);
                });
        };
        init();
    }, [params.geoZoneId, params.id]);

    const handleGetListGeoZoneByView = async (_filter: IGetGeoZoneProps) => {
        setLoadingListGzByView(true);
        await geoZoneController
            .listGeoZoneByCameraView(_filter)
            .then((res) => {
                setListGeoZonesByView(res);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setLoadingListGzByView(false);
            });
    };

    useEffect(() => {
        if (camera && Object.keys(camera).length > 0) {
            handleGetListGeoZoneByView(filteredListGeoZones);
        }
    }, [filteredListGeoZones, geoZonesItem, cameraView]);

    const navigateToCamera = () => {
        navigate(`${pathNameBreadCrumbs}/${params.id}`, { state: { tabValue: !_.isNil(camera.PTZCameraId) ? 2 : 1 } });
    };

    const getCameraView = async () => {
        const cameraViewId = (geoZonesItem?.CameraViewId ?? selectedGeoZone?.CameraViewId)?.toString();

        if (!cameraViewId) {
            return navigateToCamera();
        }

        try {
            const res = await cameraViewController.get(cameraViewId);
            setCameraView(res);
        } catch (error) {
            navigateToCamera();
        }
    };

    useEffect(() => {
        if (!cameraView && camera.PTZCameraId) {
            getCameraView();
        }
    }, [geoZonesItem, cameraView, selectedGeoZone]);

    useEffect(() => {
        const convertBase64ToImage = async () => {
            try {
                const imgElement = await base64ToHTMLImageElement(blobBase64SP111);
                setImageElementSP111(imgElement);
            } catch (error) {
                console.error('Failed to convert base64 to image', error);
            }
        };
        convertBase64ToImage();
    }, [blobBase64SP111]);

    return {
        navigate,
        isValid,
        isDirty,
        methods: methods as any,
        loading,
        isLoading,
        isLoadingSave,
        isEdit,
        conditionBreadcrumbs,
        selectedTab,
        setSelectedTab,
        mode,
        setMode,
        selectedScheduleOperationHours,
        setSelectedScheduleOperationHours,

        contraventionAutocomplete,
        detectModeAutocomplete,
        locationAutocomplete,
        zoneAutocomplete,
        zones,
        setZones,

        _nextOperationHours,
        geoZonesItem: geoZonesItem ?? selectedGeoZone,
        selectedGeoZone,
        _currentOperationHours,
        currentOperationHours,
        currentGeoZoneConfigs,
        currentSelectedPeriodConfigs,
        filterPreGeoZones,
        filterPreGeoZonesCameraSP111,
        imageElement,
        imageElementSP111,
        scheduleOperationConfigs,
        popUpEditablePeriods,
        popUpDeletePeriods,
        nextGeoZoneConfigs,
        nextOperationHours,
        nextPeriodConfigs,

        handleConfirm,
        handleDelete,
        handleEditGeoZone,
        handleNavigateButton,

        loadingListGzByView,
    };
}
